import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SelectionPicker } from "../../components/game/SelectionPicker.js";
import { fetchScope, saveScope, type ScopeView } from "../../services/http.js";

/**
 * What this installation serves — phase 16g-2.
 *
 * `/setup` writes the scope once at first run; this is how it changes
 * afterwards. **Both, decided with the maintainer**: whoever runs setup has not
 * ingested anything yet, so their answer is a guess, and a guess made permanent
 * is a guess that stays wrong.
 *
 * # ⚠️ Nothing chosen means everything
 *
 * The same rule as a party's selection, one level up — so an empty screen is a
 * correct, playable installation rather than an unconfigured one, and clearing
 * every chip is how a narrowing is undone.
 *
 * # ⚠️ Narrowing does not rewrite anybody's party
 *
 * A party that had chosen a genre this installation no longer serves keeps the
 * row and draws nothing from it. Widening again restores exactly what was
 * there, which is why this screen only ever writes its own two tables.
 */
export function ScopePanel() {
  const { t } = useTranslation();
  const [scope, setScope] = useState<ScopeView | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchScope().then(
      (loaded) => {
        if (!cancelled) {
          setScope(loaded);
        }
      },
      () => {
        if (!cancelled) {
          setMissing(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (scope === null) {
    return <p className="text-sm text-white/50">{t("scope.reading")}</p>;
  }

  const apply = (next: { genres: string[]; decades: number[] }) => {
    // Optimistic, then corrected by the count the server sends back. The chips
    // have to move on the tap or the screen reads as broken on a slow link, and
    // the only thing that can be wrong is a number.
    setScope({
      ...scope,
      genres: scope.genres.map((genre) => ({
        ...genre,
        chosen: next.genres.includes(genre.id),
      })),
      chosen_decades: next.decades,
      serves_everything: next.genres.length === 0 && next.decades.length === 0,
    });
    setBusy(true);
    setSaved(false);
    void saveScope(next.genres, next.decades)
      .then((reply) => {
        setScope((held) =>
          held === null ? held : { ...held, playable_tracks: reply.playable_tracks },
        );
        setSaved(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section aria-label={t("scope.title")}>
      <p className="mb-3 text-sm text-white/60">{t("scope.lead")}</p>

      <SelectionPicker
        genres={scope.genres.map((genre) => ({
          id: genre.id,
          slug: genre.slug,
          name: genre.name,
          parent_id: genre.parent_id,
        }))}
        decades={scope.decades}
        chosenGenres={scope.genres.filter((g) => g.chosen).map((g) => g.id)}
        chosenDecades={scope.chosen_decades}
        playableTracks={scope.playable_tracks}
        disabled={busy}
        onChange={apply}
      />

      {saved && !busy && (
        <p role="status" className="mt-3 text-xs text-flash-400">
          {t("scope.saved")}
        </p>
      )}
    </section>
  );
}
